import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, ListBoxSection, type Selection } from 'react-aria-components';
import { Plus } from 'lucide-react';
import { Badge } from 'v5/design-system/Badge';
import { Button } from 'v5/design-system/Button';
import {
  ListBox,
  ListBoxItem,
  ListBoxItemContent
} from 'v5/design-system/ListBox';
import { SearchSegmentsInput } from 'v5/patterns/filters/SearchSegments';
import { cn } from 'v5/platform/dom/cn';

export interface CustomFieldCatalogItem {
  id: number;
  name: string;
  /** Short label for the field type (e.g. humanised enum). */
  typeLabel: string;
  /** ISO 8601 instant from the API; used to sort the list newest-first. */
  created_at: string;
}

/** A field group offered beside the fields. */
export interface CustomFieldGroupCatalogItem {
  id: number;
  name: string;
  memberCount: number;
  /** People can add as many blocks of it as they need. */
  isArray: boolean;
}

/** List keys: a field and a group may share an id. */
const fieldKey = (id: number) => `field:${id}`;
const groupKey = (id: number) => `group:${id}`;

function catalogCreatedAtMs(item: CustomFieldCatalogItem): number {
  const t = Date.parse(item.created_at);
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

const EMPTY_LOCKED_ASSIGNMENT_IDS = new Set<number>();
const NO_GROUPS: CustomFieldGroupCatalogItem[] = [];

export interface CustomFieldAddSearchStageProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  catalog: CustomFieldCatalogItem[];
  value: ReadonlySet<number>;
  onChange?: (nextAssignedIds: ReadonlySet<number>) => void;
  /** Already-assigned ids that cannot be unassigned here. */
  lockedAssignmentIds?: ReadonlySet<number>;
  onOpenCreateFromSearchQuery: (prefillName: string) => void;
  /** Field groups, listed in a section of their own after the fields. */
  groupCatalog?: CustomFieldGroupCatalogItem[];
  groupValue?: ReadonlySet<number>;
  onGroupChange?: (nextAssignedIds: ReadonlySet<number>) => void;
  lockedGroupIds?: ReadonlySet<number>;
}

/** The ids of one kind among the selected keys. */
function selectedIdsOf(keys: Selection, prefix: 'field' | 'group') {
  if (keys === 'all') return null;
  return new Set(
    [...keys]
      .map(String)
      .filter(key => key.startsWith(`${prefix}:`))
      .map(key => Number(key.slice(prefix.length + 1)))
      .filter(Number.isFinite)
  );
}

/** The next assignment of one kind: shown ids follow the list, locked ones stay, hidden ones stay as they were. */
function nextAssignment(
  current: ReadonlySet<number>,
  shownIds: ReadonlySet<number>,
  selected: ReadonlySet<number>,
  locked: ReadonlySet<number>
): ReadonlySet<number> {
  const next = new Set(current);
  for (const id of shownIds) {
    if (selected.has(id)) next.add(id);
    else if (!locked.has(id)) next.delete(id);
  }
  for (const id of locked) next.add(id);
  return next;
}

export function CustomFieldAddSearchStage({
  searchQuery,
  onSearchQueryChange,
  catalog,
  value,
  onChange,
  lockedAssignmentIds = EMPTY_LOCKED_ASSIGNMENT_IDS,
  onOpenCreateFromSearchQuery,
  groupCatalog = NO_GROUPS,
  groupValue = EMPTY_LOCKED_ASSIGNMENT_IDS,
  onGroupChange,
  lockedGroupIds = EMPTY_LOCKED_ASSIGNMENT_IDS
}: CustomFieldAddSearchStageProps) {
  const { t } = useTranslation('patterns');
  const isInteractive = !!onChange;
  const isAssigned = (fieldId: number) => value.has(fieldId);
  const normalizedQuery = searchQuery.trim();
  const normalizedQueryLower = normalizedQuery.toLowerCase();

  const showCreateFromSearch =
    normalizedQuery.length > 0 &&
    !catalog.some(f => f.name.toLowerCase() === normalizedQueryLower);

  const filtered = useMemo(() => {
    const matches = normalizedQueryLower
      ? catalog.filter(f => f.name.toLowerCase().includes(normalizedQueryLower))
      : [...catalog];
    matches.sort((a, b) => catalogCreatedAtMs(b) - catalogCreatedAtMs(a));
    return matches;
  }, [catalog, normalizedQueryLower]);

  const filteredGroups = useMemo(
    () =>
      normalizedQueryLower
        ? groupCatalog.filter(group =>
            group.name.toLowerCase().includes(normalizedQueryLower)
          )
        : groupCatalog,
    [groupCatalog, normalizedQueryLower]
  );

  const hasListContent = filtered.length > 0 || filteredGroups.length > 0;
  const showEmpty =
    !hasListContent &&
    !showCreateFromSearch &&
    catalog.length === 0 &&
    groupCatalog.length === 0;
  const withSections = groupCatalog.length > 0;

  const selectedKeys = useMemo(
    () =>
      new Set([
        ...filtered
          .filter(field => value.has(field.id))
          .map(field => fieldKey(field.id)),
        ...filteredGroups
          .filter(group => groupValue.has(group.id))
          .map(group => groupKey(group.id))
      ]),
    [filtered, filteredGroups, value, groupValue]
  );

  const handleSelectionChange = (keys: Selection) => {
    const fields = selectedIdsOf(keys, 'field');
    const groups = selectedIdsOf(keys, 'group');
    if (!fields || !groups) return;
    onChange?.(
      nextAssignment(
        value,
        new Set(filtered.map(field => field.id)),
        fields,
        lockedAssignmentIds
      )
    );
    onGroupChange?.(
      nextAssignment(
        groupValue,
        new Set(filteredGroups.map(group => group.id)),
        groups,
        lockedGroupIds
      )
    );
  };

  const fieldRows = filtered.map(field => (
    <CatalogFieldRow
      key={fieldKey(field.id)}
      field={field}
      isAssigned={isAssigned(field.id)}
      isInteractive={isInteractive}
      lockedReason={
        lockedAssignmentIds.has(field.id)
          ? t('CustomFieldAddFlow.alreadyAdded')
          : null
      }
    />
  ));
  const groupRows = filteredGroups.map(group => (
    <CatalogGroupRow
      key={groupKey(group.id)}
      group={group}
      isAssigned={groupValue.has(group.id)}
      isInteractive={Boolean(onGroupChange)}
      lockedReason={
        lockedGroupIds.has(group.id)
          ? t('CustomFieldAddFlow.alreadyAdded')
          : null
      }
    />
  ));
  const sectionHeaderClassName =
    'px-5 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted';

  const renderListContent = () => {
    if (showEmpty) {
      return (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-fg-muted">
            {t('CustomFieldAddFlow.noCatalog')}
          </p>
        </div>
      );
    }

    if (!hasListContent && !showCreateFromSearch) {
      return (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-fg-muted">
            {t(
              'CustomFieldAddFlow.noFieldsAvailable',
              normalizedQuery ? { context: 'search' } : undefined
            )}
          </p>
        </div>
      );
    }

    if (!hasListContent) {
      return null;
    }

    return (
      <div className="py-1">
        <p className="px-5 py-2 text-sm font-medium text-fg-muted">
          {t('CustomFieldAddFlow.selectFieldsToAdd')}
        </p>
        <ListBox
          aria-label={t('CustomFieldAddFlow.selectFieldsToAdd')}
          className="py-1 divide-y-0"
          selectionMode="multiple"
          selectionBehavior="toggle"
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
        >
          {withSections ? (
            <>
              {fieldRows.length > 0 && (
                <ListBoxSection>
                  <Header className={sectionHeaderClassName}>
                    {t('CustomFieldAddFlow.fieldsSection')}
                  </Header>
                  {fieldRows}
                </ListBoxSection>
              )}
              {groupRows.length > 0 && (
                <ListBoxSection>
                  <Header className={sectionHeaderClassName}>
                    {t('CustomFieldAddFlow.groupsSection')}
                  </Header>
                  {groupRows}
                </ListBoxSection>
              )}
            </>
          ) : (
            fieldRows
          )}
        </ListBox>
      </div>
    );
  };

  return (
    <>
      <div className="border-b border-border-default px-3 py-3">
        <SearchSegmentsInput
          placeholder={t('CustomFieldAddFlow.searchPlaceholder')}
          value={searchQuery}
          onChange={onSearchQueryChange}
        />
      </div>

      {showCreateFromSearch && (
        <div className="px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenCreateFromSearchQuery(normalizedQuery)}
            className="flex w-full items-center gap-3 px-3 py-2 text-left"
          >
            <Plus className="size-4 shrink-0 text-fg-muted" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {t('CustomFieldAddFlow.createNamed', {
                  name: normalizedQuery
                })}
              </p>
              <p className="text-xs text-fg-muted">
                {t('CustomFieldAddFlow.addAsNewHint')}
              </p>
            </div>
          </Button>
        </div>
      )}

      <div className="max-h-[min(320px,50vh)] overflow-y-auto">
        {renderListContent()}
      </div>
    </>
  );
}

function CatalogFieldRow({
  field,
  isAssigned,
  isInteractive,
  lockedReason
}: {
  field: CustomFieldCatalogItem;
  isAssigned: boolean;
  isInteractive: boolean;
  lockedReason: string | null;
}) {
  const isSelectionLocked = lockedReason !== null;
  const lockedReasonLabel = lockedReason?.trim();
  const isRowDisabled = !isInteractive || isSelectionLocked;
  return (
    <ListBoxItem
      id={fieldKey(field.id)}
      textValue={`${field.name} ${field.typeLabel}`}
      isDisabled={isRowDisabled}
      className={cn(
        'flex w-full items-center gap-3 px-5 py-2.5 text-left last:rounded-b-none',
        isRowDisabled && 'opacity-70',
        !isAssigned && 'hover:bg-bg-accent/30',
        isSelectionLocked && 'cursor-default'
      )}
    >
      <ListBoxItemContent
        primary={
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-base font-medium">{field.name}</span>
            <Badge
              variant="gray"
              tone="subtle"
              size="sm"
              className="shrink-0 border border-border-default px-1.5 py-0 text-xs font-normal uppercase tracking-wide"
            >
              {field.typeLabel}
            </Badge>
          </div>
        }
        extra={
          isSelectionLocked && lockedReasonLabel ? (
            <Badge variant="brand" tone="subtle" size="sm">
              {lockedReasonLabel}
            </Badge>
          ) : undefined
        }
      />
    </ListBoxItem>
  );
}

function CatalogGroupRow({
  group,
  isAssigned,
  isInteractive,
  lockedReason
}: {
  group: CustomFieldGroupCatalogItem;
  isAssigned: boolean;
  isInteractive: boolean;
  lockedReason: string | null;
}) {
  const { t } = useTranslation('patterns');
  const isSelectionLocked = lockedReason !== null;
  const isRowDisabled = !isInteractive || isSelectionLocked;
  return (
    <ListBoxItem
      id={groupKey(group.id)}
      textValue={group.name}
      isDisabled={isRowDisabled}
      className={cn(
        'flex w-full items-center gap-3 px-5 py-2.5 text-left last:rounded-b-none',
        isRowDisabled && 'opacity-70',
        !isAssigned && 'hover:bg-bg-accent/30',
        isSelectionLocked && 'cursor-default'
      )}
    >
      <ListBoxItemContent
        primary={
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-base font-medium">{group.name}</span>
            <Badge
              variant="gray"
              tone="subtle"
              size="sm"
              className="shrink-0 border border-border-default px-1.5 py-0 text-xs font-normal uppercase tracking-wide"
            >
              {t('CustomFieldAddFlow.groupFieldCount', {
                count: group.memberCount
              })}
            </Badge>
            {group.isArray && (
              <Badge
                variant="gray"
                tone="subtle"
                size="sm"
                className="shrink-0 border border-border-default px-1.5 py-0 text-xs font-normal uppercase tracking-wide"
              >
                {t('CustomFieldAddFlow.groupMultiple')}
              </Badge>
            )}
          </div>
        }
        extra={
          isSelectionLocked ? (
            <Badge variant="brand" tone="subtle" size="sm">
              {lockedReason}
            </Badge>
          ) : undefined
        }
      />
    </ListBoxItem>
  );
}
